/**
 * LoadingSpinner - Consistent loading indicator across the app.
 *
 * Usage:
 *   <LoadingSpinner />                    // Default medium size
 *   <LoadingSpinner size="sm" />          // Small (16px)
 *   <LoadingSpinner size="lg" />          // Large (48px)
 *   <LoadingSpinner message="Loading..." />  // With message
 *   <LoadingSpinner fullPage />           // Centered full page
 */

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  message?: string
  fullPage?: boolean
  className?: string
}

const sizeClasses = {
  sm: 'h-4 w-4 border-2',
  md: 'h-8 w-8 border-3',
  lg: 'h-12 w-12 border-4',
  xl: 'h-16 w-16 border-4',
}

export default function LoadingSpinner({
  size = 'md',
  message,
  fullPage = false,
  className = '',
}: LoadingSpinnerProps) {
  const spinner = (
    <div className={`flex flex-col items-center justify-center gap-3 ${className}`}>
      <div
        className={`animate-spin rounded-full border-primary-200 border-t-primary-600 ${sizeClasses[size]}`}
        role="status"
        aria-label="Loading"
      />
      {message && (
        <p className="text-sm text-gray-600 animate-pulse">{message}</p>
      )}
    </div>
  )

  if (fullPage) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        {spinner}
      </div>
    )
  }

  return spinner
}
